<template>
  <el-dialog
    custom-class="phone-role-prompt-dialog"
    :visible="true"
    width="min(860px, 94vw)"
    top="4vh"
    append-to-body
    :before-close="close"
    :show-close="!saving"
    :close-on-click-modal="false"
    :close-on-press-escape="!saving"
  >
    <!-- 自定义精致顶部标题栏 -->
    <div slot="title" class="dialog-header-custom">
      <div class="header-left">
        <div class="header-icon-box">
          <i class="el-icon-mobile-phone"></i>
        </div>
        <div class="header-text-group">
          <div class="header-title-row">
            <span class="header-title-text">{{ $t('systemRole.phonePrompt.title', { name: role.name || role.id }) }}</span>
          </div>
          <div class="header-sub-row">
            <span class="role-badge role-id">ID: {{ role.id }}</span>
            <span v-if="role.name" class="role-badge role-name">{{ role.name }}</span>
            <span class="header-sub-desc">{{ $t('systemRole.phonePrompt.subtitle') }}</span>
          </div>
        </div>
      </div>
      <div v-if="ready" class="header-right">
        <div class="status-pill" :class="hasOverride ? 'is-active' : 'is-fallback'">
          <span class="status-dot"></span>
          <span class="status-text">
            {{ $t(hasOverride ? 'systemRole.phonePrompt.configured' : 'systemRole.phonePrompt.fallback') }}
          </span>
        </div>
      </div>
    </div>

    <!-- 弹窗主体 -->
    <div v-loading="loading" element-loading-background="rgba(15, 23, 42, 0.75)" class="phone-prompt-body">
      <!-- 范围说明卡片 -->
      <div class="scope-banner">
        <div class="banner-icon">
          <i class="el-icon-info"></i>
        </div>
        <div class="banner-body">
          <div class="banner-title">{{ $t('systemRole.phonePrompt.scopeTitle') }}</div>
          <div class="banner-desc">{{ $t('systemRole.phonePrompt.scope') }}</div>
        </div>
      </div>

      <!-- 错误警告条 -->
      <div v-if="error" class="phone-prompt-error" role="alert">
        <div class="error-banner">
          <i class="el-icon-warning-outline error-icon"></i>
          <div class="error-content">
            <div class="error-text">{{ error }}</div>
          </div>
          <el-button v-if="!ready" size="mini" type="danger" plain :disabled="loading" class="retry-btn" @click="load">
            <i class="el-icon-refresh"></i> {{ $t('systemRole.phonePrompt.retry') }}
          </el-button>
        </div>
      </div>

      <!-- 加载完成后展示内容 -->
      <template v-if="ready">
        <!-- 原系统人物 Prompt 折叠对比卡片 -->
        <div class="prompt-card original-prompt-card">
          <div class="card-header" @click="originalExpanded = !originalExpanded">
            <div class="card-header-left">
              <i class="toggle-icon el-icon-arrow-right" :class="{ 'is-expanded': originalExpanded }"></i>
              <i class="section-icon el-icon-document"></i>
              <span class="section-title">{{ $t('systemRole.phonePrompt.original') }}</span>
              <span class="prompt-len-tag">
                {{ originalPrompt ? originalPrompt.length + ' ' + $t('systemRole.phonePrompt.charCount') : $t('systemRole.phonePrompt.empty') }}
              </span>
            </div>
            <div class="card-header-actions" @click.stop>
              <el-tooltip :content="$t('systemRole.phonePrompt.copyOriginal')" placement="top">
                <el-button
                  type="text"
                  size="mini"
                  class="action-btn text-purple"
                  :disabled="saving || !originalPrompt"
                  @click="copyOriginalToInput"
                >
                  <i class="el-icon-bottom-left"></i>
                  <span>{{ $t('systemRole.phonePrompt.copyOriginal') }}</span>
                </el-button>
              </el-tooltip>
              <el-tooltip :content="$t('systemRole.phonePrompt.copyToClipboard')" placement="top">
                <el-button
                  type="text"
                  size="mini"
                  class="action-btn"
                  :disabled="!originalPrompt"
                  @click="copyToClipboard(originalPrompt)"
                >
                  <i class="el-icon-document-copy"></i>
                  <span>{{ $t('systemRole.phonePrompt.copyToClipboard') }}</span>
                </el-button>
              </el-tooltip>
            </div>
          </div>

          <el-collapse-transition>
            <div v-show="originalExpanded" class="card-collapse-content">
              <div v-if="originalPrompt" class="code-preview-wrapper">
                <pre class="original-prompt-text">{{ originalPrompt }}</pre>
              </div>
              <div v-else class="empty-original">
                <i class="el-icon-warning-outline"></i>
                <span>{{ $t('systemRole.phonePrompt.empty') }}</span>
              </div>
            </div>
          </el-collapse-transition>
        </div>

        <!-- 手机专用 Prompt 编辑区卡片 -->
        <div class="prompt-card editor-card">
          <div class="card-header">
            <div class="card-header-left">
              <span class="accent-bar"></span>
              <i class="section-icon el-icon-edit-outline"></i>
              <label class="section-title" for="phone-role-prompt">{{ $t('systemRole.phonePrompt.label') }}</label>
            </div>
            <div class="card-header-actions">
              <el-button
                v-if="prompt"
                type="text"
                size="mini"
                class="action-btn text-muted hover-danger"
                :disabled="saving"
                @click="prompt = ''"
              >
                <i class="el-icon-delete"></i>
                <span>{{ $t('systemRole.phonePrompt.clearText') }}</span>
              </el-button>
              <div class="char-badge" :class="charCountStatus">
                <span class="count-num">{{ prompt.length }}</span>
                <span class="count-separator">/</span>
                <span class="count-max">{{ maxLength }}</span>
              </div>
            </div>
          </div>

          <div class="editor-wrapper">
            <el-input
              id="phone-role-prompt"
              ref="promptInput"
              v-model="prompt"
              type="textarea"
              :rows="13"
              :maxlength="maxLength"
              :disabled="saving"
              :placeholder="$t('systemRole.phonePrompt.placeholder')"
              class="custom-prompt-textarea"
            />
          </div>

          <div class="editor-tips-row">
            <i class="el-icon-s-opportunity tip-icon"></i>
            <span class="tip-text">{{ $t('systemRole.phonePrompt.tips') }}</span>
          </div>
        </div>
      </template>
    </div>

    <!-- 底部操作栏 -->
    <div slot="footer" class="phone-prompt-footer">
      <div class="footer-left">
        <el-popconfirm
          v-if="hasOverride"
          :title="$t('systemRole.phonePrompt.clearConfirm')"
          :confirm-button-text="$t('systemRole.phonePrompt.clear')"
          :cancel-button-text="$t('systemRole.phonePrompt.close')"
          confirm-button-type="danger"
          icon="el-icon-warning"
          icon-color="#f87171"
          popper-class="phone-role-popconfirm"
          @confirm="save(true)"
        >
          <el-button
            slot="reference"
            type="danger"
            plain
            size="medium"
            icon="el-icon-refresh-left"
            class="btn-clear-override"
            :disabled="!ready || loading || saving"
          >
            {{ $t('systemRole.phonePrompt.clear') }}
          </el-button>
        </el-popconfirm>
      </div>
      <div class="footer-right">
        <el-button
          size="medium"
          class="btn-dialog-close"
          :disabled="saving"
          @click="close"
        >
          {{ $t('systemRole.phonePrompt.close') }}
        </el-button>
        <el-button
          type="primary"
          size="medium"
          icon="el-icon-check"
          class="btn-dialog-save"
          :loading="saving"
          :disabled="!ready || loading || saving || !prompt.trim()"
          @click="save(false)"
        >
          {{ $t('systemRole.phonePrompt.save') }}
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import api from '@/network/systemRole'

export default {
  name: 'PhoneRolePromptDialog',
  props: {
    role: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      ready: false,
      error: '',
      prompt: '',
      originalPrompt: '',
      hasOverride: false,
      maxLength: 12000,
      originalExpanded: false
    }
  },
  computed: {
    charCountStatus() {
      const ratio = this.prompt.length / this.maxLength
      if (ratio >= 1) return 'is-danger'
      if (ratio >= 0.9) return 'is-warning'
      return 'is-normal'
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    close() {
      if (!this.saving) {
        this.$emit('close')
      }
    },
    apply(view) {
      this.originalPrompt = (view && view.role && view.role.prompt) ? view.role.prompt : (this.role.prompt || '')
      this.prompt = (view && view.phonePrompt) ? view.phonePrompt : ''
      this.hasOverride = !!this.prompt.trim()
    },
    async load() {
      this.loading = true
      this.ready = false
      this.error = ''
      try {
        const [config, response] = await Promise.all([
          api.getPhonePromptConfig(),
          api.getPhonePrompt(this.role.id)
        ])
        if (this._isDestroyed) return
        this.maxLength = Number(config.data && config.data.promptMaxLength) || 12000
        this.apply(response.data)
        this.ready = true
      } catch (error) {
        this.error = error.message || this.$t('systemRole.phonePrompt.loadFailed')
      } finally {
        this.loading = false
      }
    },
    copyOriginalToInput() {
      if (!this.originalPrompt) return
      if (this.prompt && this.prompt.trim() && this.prompt !== this.originalPrompt) {
        this.$confirm(
          this.$t('systemRole.phonePrompt.copyOriginalConfirm'),
          this.$t('common.tips') || '提示',
          {
            confirmButtonText: this.$t('systemRole.phonePrompt.copyOriginal'),
            cancelButtonText: this.$t('systemRole.phonePrompt.close'),
            type: 'warning',
            customClass: 'phone-role-confirm-box'
          }
        ).then(() => {
          this.prompt = this.originalPrompt
          this.$message.success(this.$t('systemRole.phonePrompt.copyOriginalSuccess'))
        }).catch(() => {})
      } else {
        this.prompt = this.originalPrompt
        this.$message.success(this.$t('systemRole.phonePrompt.copyOriginalSuccess'))
      }
    },
    copyToClipboard(text) {
      if (!text) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success(this.$t('systemRole.phonePrompt.copySuccess'))
        }).catch(() => {
          this.fallbackCopy(text)
        })
      } else {
        this.fallbackCopy(text)
      }
    },
    fallbackCopy(text) {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.left = '-9999px'
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand('copy')
        this.$message.success(this.$t('systemRole.phonePrompt.copySuccess'))
      } catch (err) {
        this.$message.error(this.$t('systemRole.phonePrompt.saveFailed'))
      } finally {
        document.body.removeChild(ta)
      }
    },
    async save(clear) {
      if (!this.ready || this.saving || this.loading) return
      const prompt = this.prompt.trim()
      if (!clear && (!prompt || prompt.length > this.maxLength)) {
        this.error = this.$t('systemRole.phonePrompt.invalid', { max: this.maxLength })
        return
      }
      this.saving = true
      this.error = ''
      try {
        const response = clear
          ? await api.clearPhonePrompt(this.role.id)
          : await api.savePhonePrompt(this.role.id, prompt)
        if (this._isDestroyed) return
        this.apply(response.data)
        this.$message.success(
          this.$t(clear ? 'systemRole.phonePrompt.clearSuccess' : 'systemRole.phonePrompt.saveSuccess')
        )
      } catch (error) {
        this.error = error.message || this.$t('systemRole.phonePrompt.saveFailed')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<!-- 非 scoped 样式：针对 append-to-body 渲染的 el-dialog 容器与 Element 组件进行深色磨砂玻璃重构 -->
<style>
.phone-role-prompt-dialog {
  background: rgba(22, 28, 45, 0.96) !important;
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  border: 1px solid rgba(75, 85, 99, 0.45) !important;
  border-radius: 20px !important;
  box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.8), 0 0 40px rgba(124, 58, 237, 0.15) !important;
  overflow: hidden;
  margin-bottom: 4vh !important;
}

/* Header */
.phone-role-prompt-dialog .el-dialog__header {
  padding: 18px 24px !important;
  background: linear-gradient(180deg, rgba(30, 41, 59, 0.75) 0%, rgba(15, 23, 42, 0.5) 100%) !important;
  border-bottom: 1px solid rgba(51, 65, 85, 0.6) !important;
  position: relative;
}

.phone-role-prompt-dialog .el-dialog__headerbtn {
  top: 18px !important;
  right: 20px !important;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(51, 65, 85, 0.25);
  border: 1px solid rgba(75, 85, 99, 0.35);
  transition: all 0.2s ease;
}

.phone-role-prompt-dialog .el-dialog__headerbtn:hover {
  background: rgba(239, 68, 68, 0.18);
  border-color: rgba(239, 68, 68, 0.4);
}

.phone-role-prompt-dialog .el-dialog__headerbtn .el-dialog__close {
  color: #94a3b8 !important;
  font-size: 16px;
  font-weight: 700;
  transition: color 0.2s, transform 0.2s;
}

.phone-role-prompt-dialog .el-dialog__headerbtn:hover .el-dialog__close {
  color: #f87171 !important;
  transform: rotate(90deg);
}

/* Body */
.phone-role-prompt-dialog .el-dialog__body {
  padding: 20px 24px !important;
  color: #e2e8f0 !important;
  max-height: calc(88vh - 140px);
  overflow-y: auto;
}

.phone-role-prompt-dialog .el-dialog__body::-webkit-scrollbar {
  width: 6px;
}
.phone-role-prompt-dialog .el-dialog__body::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.4);
}
.phone-role-prompt-dialog .el-dialog__body::-webkit-scrollbar-thumb {
  background: rgba(139, 92, 246, 0.35);
  border-radius: 3px;
}
.phone-role-prompt-dialog .el-dialog__body::-webkit-scrollbar-thumb:hover {
  background: rgba(139, 92, 246, 0.6);
}

/* Footer */
.phone-role-prompt-dialog .el-dialog__footer {
  padding: 16px 24px !important;
  background: rgba(15, 23, 42, 0.6) !important;
  border-top: 1px solid rgba(51, 65, 85, 0.6) !important;
}

/* Textarea inside dialog */
.phone-role-prompt-dialog .custom-prompt-textarea .el-textarea__inner {
  background-color: rgba(11, 15, 25, 0.85) !important;
  border: 1px solid rgba(51, 65, 85, 0.8) !important;
  border-radius: 12px !important;
  color: #f8fafc !important;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace !important;
  font-size: 13.5px !important;
  line-height: 1.65 !important;
  padding: 14px 16px !important;
  transition: all 0.25s ease !important;
}

.phone-role-prompt-dialog .custom-prompt-textarea .el-textarea__inner:focus {
  border-color: #8b5cf6 !important;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.25) !important;
  background-color: rgba(11, 15, 25, 0.95) !important;
}

.phone-role-prompt-dialog .custom-prompt-textarea .el-textarea__inner::placeholder {
  color: #64748b !important;
}

.phone-role-prompt-dialog .custom-prompt-textarea .el-textarea__inner::-webkit-scrollbar {
  width: 6px;
}
.phone-role-prompt-dialog .custom-prompt-textarea .el-textarea__inner::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.4);
}
.phone-role-prompt-dialog .custom-prompt-textarea .el-textarea__inner::-webkit-scrollbar-thumb {
  background: rgba(139, 92, 246, 0.4);
  border-radius: 3px;
}
.phone-role-prompt-dialog .custom-prompt-textarea .el-textarea__inner::-webkit-scrollbar-thumb:hover {
  background: rgba(139, 92, 246, 0.6);
}

/* Popconfirm popover styling */
.el-popover.phone-role-popconfirm {
  background: #1e293b !important;
  border: 1px solid #334155 !important;
  color: #f1f5f9 !important;
  border-radius: 12px !important;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.6) !important;
}
.el-popover.phone-role-popconfirm .el-popconfirm__main {
  color: #f1f5f9 !important;
  margin-bottom: 12px;
}

/* MessageBox styling */
.el-message-box.phone-role-confirm-box {
  background: #1e293b !important;
  border: 1px solid #334155 !important;
  border-radius: 14px !important;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.6) !important;
}
.el-message-box.phone-role-confirm-box .el-message-box__title {
  color: #f8fafc !important;
}
.el-message-box.phone-role-confirm-box .el-message-box__content {
  color: #cbd5e1 !important;
}
</style>

<!-- Scoped 样式：组件布局与细节 -->
<style scoped>
.phone-prompt-body {
  min-height: 180px;
}

/* Header */
.dialog-header-custom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-right: 36px;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.header-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.25) 0%, rgba(99, 102, 241, 0.15) 100%);
  border: 1px solid rgba(139, 92, 246, 0.4);
  color: #c4b5fd;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 16px rgba(139, 92, 246, 0.2);
  flex-shrink: 0;
}
.header-text-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.header-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.header-title-text {
  font-size: 17px;
  font-weight: 700;
  color: #f8fafc;
  letter-spacing: -0.01em;
}
.header-sub-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.role-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 4px;
}
.role-badge.role-id {
  background: rgba(139, 92, 246, 0.15);
  border: 1px solid rgba(139, 92, 246, 0.35);
  color: #c4b5fd;
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-weight: 600;
}
.role-badge.role-name {
  background: rgba(51, 65, 85, 0.4);
  border: 1px solid rgba(75, 85, 99, 0.5);
  color: #cbd5e1;
}
.header-sub-desc {
  font-size: 12px;
  color: #64748b;
}

/* Status pill in header */
.header-right {
  flex-shrink: 0;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s ease;
}
.status-pill.is-active {
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.35);
  color: #34d399;
}
.status-pill.is-fallback {
  background: rgba(148, 163, 184, 0.1);
  border: 1px solid rgba(148, 163, 184, 0.25);
  color: #94a3b8;
}
.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}
.status-pill.is-active .status-dot {
  background: #10b981;
  box-shadow: 0 0 8px #10b981;
  animation: pulse-status 2s infinite;
}
.status-pill.is-fallback .status-dot {
  background: #94a3b8;
}

@keyframes pulse-status {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  70% {
    transform: scale(1.1);
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

/* Scope Banner */
.scope-banner {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.04) 100%);
  border: 1px solid rgba(99, 102, 241, 0.25);
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}
.banner-icon {
  color: #818cf8;
  font-size: 18px;
  margin-top: 1px;
  flex-shrink: 0;
}
.banner-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.banner-title {
  font-size: 13px;
  font-weight: 600;
  color: #c7d2fe;
}
.banner-desc {
  font-size: 12.5px;
  color: #94a3b8;
  line-height: 1.5;
}

/* Error Banner */
.phone-prompt-error {
  margin-bottom: 16px;
}
.error-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 10px;
  padding: 10px 14px;
}
.error-icon {
  color: #f87171;
  font-size: 18px;
  flex-shrink: 0;
}
.error-content {
  flex: 1;
}
.error-text {
  font-size: 13px;
  color: #fca5a5;
}
.retry-btn {
  border-radius: 6px;
}

/* Cards (Original Prompt & Editor) */
.prompt-card {
  background: rgba(15, 23, 42, 0.45);
  border: 1px solid rgba(51, 65, 85, 0.6);
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 16px;
  transition: border-color 0.2s ease;
}
.prompt-card:hover {
  border-color: rgba(99, 102, 241, 0.35);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 16px;
  background: rgba(30, 41, 59, 0.35);
  border-bottom: 1px solid rgba(51, 65, 85, 0.4);
}
.card-header-left {
  display: flex;
  align-items: center;
}
.toggle-icon {
  color: #94a3b8;
  font-size: 13px;
  margin-right: 8px;
  transition: transform 0.2s ease;
}
.toggle-icon.is-expanded {
  transform: rotate(90deg);
  color: #c4b5fd;
}
.section-icon {
  color: #8b5cf6;
  font-size: 15px;
  margin-right: 8px;
}
.section-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #e2e8f0;
}
.accent-bar {
  width: 3px;
  height: 14px;
  background: linear-gradient(to bottom, #8b5cf6, #6d28d9);
  border-radius: 2px;
  margin-right: 8px;
}
.prompt-len-tag {
  display: inline-block;
  font-size: 11px;
  padding: 1px 8px;
  border-radius: 10px;
  background: rgba(51, 65, 85, 0.5);
  color: #94a3b8;
  margin-left: 10px;
}

/* Header actions in cards */
.card-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.action-btn {
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 6px;
  transition: all 0.2s;
  color: #94a3b8;
}
.action-btn:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #f8fafc;
}
.action-btn.text-purple {
  color: #c4b5fd;
}
.action-btn.text-purple:hover {
  color: #e9d5ff;
  background: rgba(139, 92, 246, 0.15);
}
.action-btn.hover-danger:hover {
  color: #f87171;
  background: rgba(239, 68, 68, 0.12);
}

/* Original prompt collapse */
.original-prompt-card .card-header {
  cursor: pointer;
  user-select: none;
}
.original-prompt-card .card-header:hover {
  background: rgba(30, 41, 59, 0.55);
}
.card-collapse-content {
  border-top: 1px solid rgba(51, 65, 85, 0.4);
}
.code-preview-wrapper {
  background: #090d16;
  padding: 12px 16px;
  max-height: 190px;
  overflow-y: auto;
}
.original-prompt-text {
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12.5px;
  line-height: 1.6;
  color: #93c5fd;
  white-space: pre-wrap;
  word-break: break-word;
}
.empty-original {
  padding: 20px;
  text-align: center;
  color: #64748b;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: #090d16;
}

/* Word counter badge */
.char-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-family: ui-monospace, monospace;
  padding: 2px 10px;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(51, 65, 85, 0.7);
  transition: all 0.2s;
}
.char-badge.is-normal {
  color: #94a3b8;
}
.char-badge.is-warning {
  color: #fbbf24;
  border-color: rgba(251, 191, 36, 0.4);
  background: rgba(251, 191, 36, 0.08);
}
.char-badge.is-danger {
  color: #f87171;
  border-color: rgba(248, 113, 113, 0.4);
  background: rgba(248, 113, 113, 0.08);
}
.count-separator {
  margin: 0 2px;
  opacity: 0.5;
}

/* Editor container */
.editor-wrapper {
  padding: 12px 14px 4px;
}
.editor-tips-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  background: rgba(15, 23, 42, 0.3);
  border-top: 1px solid rgba(51, 65, 85, 0.35);
  font-size: 12px;
  color: #94a3b8;
}
.tip-icon {
  color: #eab308;
  font-size: 14px;
  flex-shrink: 0;
}
.tip-text {
  line-height: 1.4;
  color: #94a3b8;
}

/* Footer buttons */
.phone-prompt-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}
.footer-right {
  display: flex;
  align-items: center;
  gap: 10px;
}
.btn-clear-override {
  background: rgba(239, 68, 68, 0.1) !important;
  border: 1px solid rgba(239, 68, 68, 0.35) !important;
  color: #fca5a5 !important;
  border-radius: 10px !important;
  font-weight: 500;
  transition: all 0.2s ease !important;
}
.btn-clear-override:hover {
  background: rgba(239, 68, 68, 0.2) !important;
  border-color: rgba(239, 68, 68, 0.6) !important;
  color: #ffffff !important;
  transform: translateY(-1px);
}
.btn-dialog-close {
  background: rgba(51, 65, 85, 0.35) !important;
  border: 1px solid rgba(71, 85, 105, 0.6) !important;
  color: #cbd5e1 !important;
  border-radius: 10px !important;
  font-weight: 500;
  transition: all 0.2s ease !important;
}
.btn-dialog-close:hover {
  background: rgba(51, 65, 85, 0.6) !important;
  color: #f8fafc !important;
}
.btn-dialog-save {
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%) !important;
  border: none !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  border-radius: 10px !important;
  padding: 10px 22px !important;
  box-shadow: 0 4px 16px 0 rgba(139, 92, 246, 0.4) !important;
  transition: all 0.2s ease !important;
}
.btn-dialog-save:hover {
  box-shadow: 0 6px 24px 0 rgba(139, 92, 246, 0.6) !important;
  transform: translateY(-1px);
}
.btn-dialog-save:active {
  transform: translateY(1px);
}
.btn-dialog-save.is-disabled {
  opacity: 0.5;
  box-shadow: none !important;
  transform: none !important;
}
</style>
